import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal } from '@syneto/compass-react';
import { pushDangerNotification, pushSuccessfulNotification } from '../../../../../data/notifications/actions';
import { deleteProductAddons } from '../../../../../services/products';
import { getJob } from '../../../../../services/jobs';
import { getApiErrorMessage } from '../../../../../helpers/apiError';
import { protectedLicensesAmong } from '../licenseRules';

const listNames = (licenses) => {
	return licenses.map((license) => { return license.name || license.code || license.id; }).join(', ');
};

/*
 * Confirms the deletion of one or several licenses. A protected license (one for machines on 6.1 or later, or one
 * Central ships with) blocks the whole dialog, as the server would refuse the whole batch anyway. The deletion of any
 * other license also takes it away from every product that has it, and the dialog says so.
 */
export class DeleteModalClass extends React.Component {
	render() {
		const { addons, onHide } = this.props;
		const protectedLicenses = protectedLicensesAmong(addons);
		const canDelete = addons.length > 0 && protectedLicenses.length === 0;

		return (
			<Modal
				show={true}
				onHide={onHide}
				title={this.title(addons)}
				content={this.content(addons, protectedLicenses)}
				actions={canDelete ?
					{
						primary: {
							label: 'Delete',
							onClick: this.onSubmit
						},
						secondary: {
							label: 'Cancel',
							onClick: onHide
						}
					} :
					{
						primary: {
							label: 'OK',
							onClick: onHide
						}
					}
				}
			/>
		);
	}

	title = (addons) => {
		if (addons.length > 1) {
			return 'Delete licenses';
		}

		return 'Delete license';
	};

	content = (addons, protectedLicenses) => {
		if (addons.length === 0) {
			return 'You have not selected any product license for deletion.';
		}

		if (protectedLicenses.length > 0) {
			return (
				<p className="text-value">
					{protectedLicenses.length === 1 ? 'This license cannot be deleted: ' : 'These licenses cannot be deleted: '}
					<strong>{listNames(protectedLicenses)}</strong>.
					{' '}Licenses for machines on 6.1 or later and the licenses Central ships with are protected.
					{addons.length > protectedLicenses.length ? ' Unselect them to delete the others.' : ''}
				</p>
			);
		}

		if (addons.length === 1) {
			return (
				<p className="text-value">
					You are going to delete the license <strong>{listNames(addons)}</strong>. It is also removed from every product that has it. Are you sure?
				</p>
			);
		}

		return (
			<p className="text-value">
				You are going to delete {addons.length} licenses. They are also removed from every product that has them. Are you sure?
			</p>
		);
	};

	onSubmit = () => {
		const { addons, onHide, afterUpdateCallback } = this.props;

		let payload = addons.map((obj) => { return obj.id; } );

		deleteProductAddons(payload)
			.then((updateResponse) => {
				const jobId = updateResponse?.data?.jobId;

				// function to poll the job status every second
				function pollJobStatus() {
					getJob(jobId)
						.then((jobResponse) => {
							if (jobResponse.data.status.toLowerCase() === 'succeeded' || jobResponse.data.status.toLowerCase() === 'failed') {
								afterUpdateCallback(); // call the callback when succeeded
							} else {
								setTimeout(pollJobStatus, 1000); // poll again in 1 second
							}
						})
						.catch((error) => {
							setTimeout(pollJobStatus, 1000); // retry polling in case of error
						});
				}

				// start polling the job status
				pollJobStatus();

				onHide();
				this.props.pushSuccessfulNotification(
					payload.length > 1 ? 'Licenses deleted successfully.' : 'License deleted successfully.'
				);
			})
			.catch((error) => {
				// A refusal (409 for a protected license or one an edition uses) carries the sentence to show.
				this.props.pushDangerNotification(
					getApiErrorMessage(error, payload.length > 1 ? 'Could not delete licenses.' : 'Could not delete license.')
				);
			});
	};
}

const mapStateToProps = ({ productMaintenanceService }) => {
	return {
		success: productMaintenanceService.success,
		error: productMaintenanceService.error
	};
};

export const DeleteModal = connect(mapStateToProps, {
	pushSuccessfulNotification,
	pushDangerNotification

})(DeleteModalClass);

DeleteModal.propTypes = {
	onHide: PropTypes.func.isRequired,
	addons: PropTypes.array,
	afterUpdateCallback: PropTypes.func.isRequired
};
